import { Link } from "react-router-dom";
import { useCallback } from "react";
import {MapPin, Calendar, Euro, ArrowUpRight, Heart} from 'lucide-react';
import toast from "react-hot-toast";
import { Job, CONTRACT_LABELS } from "@/data/jobs";
import { buildJobPath } from "@/lib/jobUrl";
import { useFavoriteJobs } from "@/hooks/useFavoriteJobs";

/**
 * JobCardHome — variante premium éditoriale du JobCard, dédiée à la home.
 * Différences vs JobCard standard :
 * - Bordure ultra-fine, ombre en couches plus douces, hover translateY -3px
 * - Accent bar latéral (au lieu du top) pour un look plus magazine
 * - Hiérarchie typo plus marquée : eyebrow couleur silo + titre tracking serré
 * - Footer minimal avec lien "Voir l'offre" en arrow up-right (style Linear/Vercel)
 * - Utilise les classes premium de index.css (.card-premium, .ease-premium)
 */

interface JobCardHomeProps {
  job: Job;
}

const formatPublishedAgo = (iso: string): { label: string; isFresh: boolean } => {
  if (!iso) return { label: "", isFresh: false };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { label: "", isFresh: false };

  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMin = Math.floor(diffMs / (1000 * 60));
  const diffH = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  let label = "";
  if (diffMs < 0) {
    label = d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  } else if (diffMin < 1) label = "À l'instant";
  else if (diffH < 1) label = `${diffMin} min`;
  else if (diffH < 24) label = `${diffH} h`;
  else if (diffDays === 1) label = "Hier";
  else if (diffDays < 7) label = `${diffDays} j`;
  else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    label = `${weeks} sem.`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    label = `${months} mois`;
  } else {
    const years = Math.floor(diffDays / 365);
    label = `${years} an${years > 1 ? "s" : ""}`;
  }

  return { label, isFresh: diffDays < 7 };
};

export default function JobCardHome({ job }: JobCardHomeProps) {
  const isBNSSA = job.category === "BNSSA";
  const jobPath = buildJobPath(job);
  const accentColor = isBNSSA ? "#dc2626" : "#2563eb";

  const { isFavorite: getIsFavorite, toggleFavorite: toggleFav } = useFavoriteJobs();
  const isFavorite = getIsFavorite(job.id);

  const toggleFavorite = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const added = toggleFav(job.id);
      toast.success(added ? "Ajouté à vos favoris" : "Retiré des favoris", {
        duration: 1800,
        icon: added ? "❤️" : "💔",
      });
    },
    [job.id, toggleFav]
  );

  const isAutoEntrepreneur = job.contractType === "auto-entrepreneur";
  const isInterim = job.contractType === "interim";
  const published = formatPublishedAgo(job.publishedAt);

  const formatMissionDate = (iso: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  };

  return (
    <article
      className="card-premium group relative overflow-hidden"
      style={{ color: accentColor }}
    >
      {/* Accent bar horizontale en haut — toujours visible */}
      <span
        aria-hidden
        className="absolute top-0 left-0 right-0 h-1"
        style={{ backgroundColor: accentColor }}
      />

      {/* Bouton favori — top right */}
      <button
        type="button"
        onClick={toggleFavorite}
        aria-pressed={isFavorite}
        aria-label={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
        className={`absolute top-4 right-4 z-10 w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-sm border transition-all duration-200 ${
          isFavorite
            ? "bg-red-50 border-red-200 text-red-500"
            : "bg-white/80 border-gray-200/60 text-gray-300 hover:text-red-500 hover:border-red-200 hover:bg-red-50"
        }`}
      >
        <Heart
          className={`w-4 h-4 transition-transform duration-200 ${isFavorite ? "fill-current scale-110" : "fill-none"}`}
          strokeWidth={2}
        />
      </button>

      <div className="p-6 text-[#0b1220]">
        {/* Eyebrow — pr-10 pour ne pas chevaucher le bouton favori */}
        <div className="flex items-center gap-2 mb-4 flex-wrap pr-10">
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: accentColor }}
          >
            {job.category}
          </span>
          <span className="w-1 h-1 rounded-full bg-gray-300" aria-hidden />
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
            {CONTRACT_LABELS[job.contractType]}
          </span>
        </div>

        {/* Slot logo + titre — alignés horizontalement */}
        <div className="flex items-start gap-4 mb-4" style={{ minHeight: "72px" }}>
          <div className="flex-1 min-w-0">
            <h3 className="text-[17px] font-bold leading-[1.25] tracking-[-0.015em] line-clamp-2 mb-1.5 text-[#0b1220] transition-colors">
              <Link to={jobPath} rel="dofollow" className="hover:opacity-80 transition-opacity">
                {job.title}
              </Link>
            </h3>
            <p className="text-[13px] text-gray-500 font-medium truncate">
              {job.employer}
            </p>
          </div>

          {/* Slot logo fixe */}
          <div
            data-logo-slot
            aria-hidden={job.logo ? undefined : true}
            className="flex-shrink-0 w-12 h-12 rounded-xl bg-white border border-gray-100 overflow-hidden flex items-center justify-center"
            style={{ visibility: job.logo ? "visible" : "hidden" }}
          >
            {job.logo && (
              <img
                src={job.logo}
                alt={`Logo ${job.employer}`}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-contain p-1"
                onError={(e) => {
                  const slot = e.currentTarget.closest("[data-logo-slot]") as HTMLElement | null;
                  if (slot) slot.style.visibility = "hidden";
                }}
              />
            )}
          </div>
        </div>

        {/* Méta-données : lieu + mission/salaire */}
        <div className="flex items-center gap-3 flex-wrap mb-4 text-[12.5px] text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-gray-350" strokeWidth={2.2} />
            <span className="truncate max-w-[180px]">{job.location}</span>
          </span>

          {(isAutoEntrepreneur || isInterim) && job.missionDate && (
            <>
              <span className="w-1 h-1 rounded-full bg-gray-300" aria-hidden />
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-gray-350" strokeWidth={2.2} />
                <span>{formatMissionDate(job.missionDate)}</span>
              </span>
            </>
          )}

          {(job.salary || job.hourlyRate || (isInterim && typeof job.missionPrice === "number")) && (
            <>
              <span className="w-1 h-1 rounded-full bg-gray-300" aria-hidden />
              <span className="inline-flex items-center gap-1.5 font-semibold text-[#0b1220] tabular">
                <Euro className="w-3.5 h-3.5 text-gray-400" strokeWidth={2.2} />
                <span className="truncate max-w-[160px]">
                  {isInterim && typeof job.missionPrice === "number"
                    ? `${job.missionPrice} € / mission`
                    : job.salary || job.hourlyRate}
                </span>
              </span>
            </>
          )}
        </div>

        {/* Description */}
        <p className="text-[13.5px] text-gray-500 leading-[1.55] line-clamp-2 mb-5">
          {job.description}
        </p>

        {/* Footer : timestamp + CTA arrow up-right */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100/80 gap-2">
          <span
            className={`flex items-center gap-1.5 text-[11.5px] font-medium tabular flex-wrap ${
              published.isFresh ? "text-emerald-600" : "text-gray-400"
            }`}
            title={`Publié le ${new Date(job.publishedAt).toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}`}
          >
            {published.isFresh && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 align-middle" />
            )}
            {published.label}
          </span>

          <Link
            to={jobPath}
            rel="nofollow"
            className="inline-flex items-center gap-1 text-[13px] font-bold transition-all duration-200 group-hover:gap-1.5"
            style={{ color: accentColor }}
            aria-label={`Voir l'offre : ${job.title}`}
          >
            Voir l'offre
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.4} />
          </Link>
        </div>
      </div>
    </article>
  );
}
